import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Import Certifications';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.CandidateDataBulkImport,
	portal: 'SP',
};

const htmlContent = `
<p><strong>To Import Certifications</strong></p>
<p>
	Include the following column headers recognized by the parser. All fields are required for a
	successful upload. Extra fields will be ignored. Omitting the required columns will result
	in an error. The max number of rows is 2,500.
</p>
<ul>
	<li>Certification External ID</li>
    <li>First Name</li>
    <li>Last Name</li>
    <li>Email Address</li>
    <li>Active Date</li>
    <li>Partner Company</li>
    <li>Country</li>
    <li>Email Domain</li>
    <li>Candidate External ID</li>
</ul>


<p>
	Below the column header lines, add a line for each candidate with a valid certification external ID and active date. If that candidate is not found, a new one will be created, if that candidate is found, their demographic data will be updated. The active date needs to be a date in the past or today. Future dates will be rejected. If a certification is added that would be active within an existing active period of that same certification for the candidate, it will be rejected. 
</p>
<p><strong>Return CSV file: </strong></p>
<p>After pressing the Submit button, a CSV file will be returned with following columns:</p>
<ul>
	<li>Certification External ID</li>
    <li>First Name</li>
    <li>Last Name</li>
    <li>Email Address</li>
    <li>Active Date</li>
    <li>Partner Company</li>
    <li>Country</li>
    <li>Email Domain</li>
    <li>Candidate External ID</li>
    <li>Import Status</li>
</ul>
<p>The status field will contain one of these values:</p>
<ul>
	<li>
		Candidate Created - Certification Created
	</li>
	<li>
		Candidate Updated - Certification Created
	</li>
	<li>Candidate ID (user email) not found</li>
    <li>Certification ID not found</li>
    <li>Active date in the future</li>
    <li>Certification already exists within active date range</li>
</ul>
`;

export type SPBulkImportCertificationsLocNames =
	| 'SPBulkImportCertificationsPageHeading'
	| 'SPBulkImportCertificationsButton'
	| 'SPBulkImportCertificationsInstructions'
	| 'SPBulkImportCertificationsMaxRowsError'
	| 'SPBulkImportCertificationsNoDataError'
	| 'SPBulkImportCertificationsInvalidDateError';

export const spBulkImportCertificationsLocs: LocDefs<SPBulkImportCertificationsLocNames> = {
	SPBulkImportCertificationsPageHeading: {
		defaultValue: 'Import Certifications',
		description: 'Page Heading for the bulk import certifications page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPBulkImportCertificationsInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the bulk import certifications page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPBulkImportCertificationsButton: {
		defaultValue: 'Import Certifications',
		description: 'Button to start importing certifications',
		displayPath: `${dpRoot}|Import Certifications Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPBulkImportCertificationsMaxRowsError: {
		defaultValue: 'You can only import 2,500 certifications at a time.',
		description: 'Error shown when the number of rows exceeds the limit for the bulk import certifications page',
		displayPath: `${dpRoot}|Max Rows Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBulkImportCertificationsNoDataError: {
		defaultValue: 'No data found in the file.',
		description: 'Error shown when the file is empty',
		displayPath: `${dpRoot}|No Data Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBulkImportCertificationsInvalidDateError: {
		defaultValue: 'Invalid date values.',
		description: 'Error shown when the active date is not in the MM/DD/YYYY format.',
		displayPath: `${dpRoot}|Invalid Date Error`,
		displayType: 'Error',
		...sharedProperties,
	},
};

export const BULK_IMPORT_CERTIFICATIONS_LOC_KEYS = MapLocKeys<SPBulkImportCertificationsLocNames>(
	spBulkImportCertificationsLocs
);
export type SPBulkImportCertificationsLocType = LocMap<SPBulkImportCertificationsLocNames>;
